import React from 'react';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';
import { formatCurrency } from '../../../../../CommonUtils/formatNumber';
import { TableSectionDTO, RowType } from '../../../../api/useBalanceSheetQuery';
import { useDetailNetWorth } from '../../useDetailNetWorth';
import { LivingBalanceSheetTable } from './Table.styles';
import { Dot } from '../../../../../CommonComponents/OPL/Dot/Dot';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';

interface Props {
  variant?: 'InEstate' | 'OutsideEstate';
  tableData: TableSectionDTO;
}

export const Table = ({ variant, tableData }: Props) => {
  const { data } = useDetailNetWorth();

  if (!data) {
    return null;
  }

  return <TableContent data={tableData} variant={variant} />;
};

interface TableContentProps {
  data: TableSectionDTO;
  variant?: 'InEstate' | 'OutsideEstate';
}
const TableContent = ({ data, variant }: TableContentProps) => {
  const { headers, rows } = data;

  if (rows.length === 0) {
    return null;
  }

  return (
    <LivingBalanceSheetTable variant="embedded">
      <table>
        <colgroup>
          {headers.map((_, index) => (
            <col key={index} width={index === 0 ? 30 : 70 / headers.length} />
          ))}
        </colgroup>

        <thead>
          <tr>
            {headers.map((header, index) => (
              <th key={header}>
                <Text variant="tableHeader" textAlign={index === 0 ? 'left' : 'right'}>
                  {header}
                </Text>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {rows.map(row => {
            if (row.rowType === RowType.Total) {
              return null;
            }

            const isGroup = row.rowType === RowType.Group;

            return (
              <tr key={row.label} className={isGroup ? 'hasBottomBorder' : ''}>
                <td>
                  <Text fontWeight={isGroup ? 600 : 400}>
                    <Box
                      display="flex"
                      alignItems="center"
                      css={({ theme }) => ({ gap: theme.space['8'] })}
                      py={isGroup ? '16' : '0'}
                    >
                      {((isGroup && row.color) || (row.color && variant === 'OutsideEstate')) && (
                        <Dot bg={row.color} />
                      )}

                      {row.label}
                    </Box>
                  </Text>
                </td>

                {row.value.map((value, index) => (
                  <td key={index}>
                    <Text
                      variant="tabular"
                      textAlign="right"
                      color={value < 0 ? 'orange.500' : 'neutral.700'}
                      fontWeight={600}
                    >
                      {value < 0 ? `(${formatCurrency(Math.abs(value))})` : formatCurrency(value)}
                    </Text>
                  </td>
                ))}
              </tr>
            );
          })}

          {rows
            .filter(({ rowType }) => rowType === RowType.Total)
            .map((row, parentIndex) => {
              return (
                <tr key={parentIndex} className="hasTopBorder">
                  <td>
                    <Text fontWeight={600}>{row.label}</Text>
                  </td>

                  {row.value.map((value, childIndex) => (
                    <td key={childIndex}>
                      <Text variant="tabular" textAlign="right" fontWeight={600}>
                        {value < 0 ? `(${formatCurrency(value)})` : formatCurrency(Math.abs(value))}
                      </Text>
                    </td>
                  ))}
                </tr>
              );
            })}
        </tbody>
      </table>
    </LivingBalanceSheetTable>
  );
};
